import React, { useMemo, useState } from 'react';
import {
  Landmark, User, Users, Building2, HardHat, Send, Timer, Clock,
  AlertTriangle, CheckCircle2, PauseCircle, ArrowRight, Calendar,
  FileQuestion, RotateCcw, Award, Play, Flag, ClipboardCheck, DollarSign,
  Hash, Briefcase, Zap, ShieldCheck, FileSignature, Flame,
} from 'lucide-react';
import { Input } from '../ui/input';
import { ZoneHeader, ZONE_STYLE, NEXT_ACTION_TONE } from './ZoneHeader';
import LenderLogo from './LenderLogo';

/**
 * Post-Lodgement Command Centre — the "application dashboard" for a lodged pack.
 *
 * Reads pack + template only (no network calls). Renders four zones:
 *   1. Application Overview strip
 *   2. Big SLA countdown card + Next Action panel (side-by-side on desktop)
 *   3. Application timeline (chronological)
 *   4. (MIR overview lives inside MIRChecklist — hooked separately)
 */
export default function PostLodgementCommandCentre({ pack, onUpdate, onFocusMirs }) {
  const derived = useMemo(() => derive(pack), [pack]);

  return (
    <div className="space-y-3" data-testid="post-lodgement-command-centre" style={{ colorScheme: 'light' }}>
      <ApplicationOverview pack={pack} derived={derived} onUpdate={onUpdate} />
      <div className="pt-2">
        <SlaCountdownCard pack={pack} derived={derived} />
      </div>
    </div>
  );
}

// Timeline is rendered separately by PackBuilder so it can sit AFTER the MIR
// section (Timeline is context; MIRs are the primary post-lodgement action).
export { ApplicationTimeline };

// ─── Palette (inline styles to defeat dark-mode inversion) ────
const T = {
  navy:    { bg: '#0f172a', ink: '#ffffff' },
  slate:   { bg: '#f8fafc', border: '#e2e8f0', ink: '#0f172a', accent: '#475569', mutedInk: '#64748b' },
  blue:    { bg: '#eff6ff', border: '#bfdbfe', ink: '#1e3a8a', accent: '#2563eb', mutedInk: '#1e40af' },
  indigo:  { bg: '#eef2ff', border: '#c7d2fe', ink: '#312e81', accent: '#4f46e5', mutedInk: '#3730a3' },
  amber:   { bg: '#fffbeb', border: '#fcd34d', ink: '#78350f', accent: '#d97706', mutedInk: '#92400e' },
  rose:    { bg: '#fff1f2', border: '#fda4af', ink: '#881337', accent: '#e11d48', mutedInk: '#9f1239' },
  emerald: { bg: '#ecfdf5', border: '#6ee7b7', ink: '#064e3b', accent: '#059669', mutedInk: '#047857' },
  teal:    { bg: '#f0fdfa', border: '#5eead4', ink: '#134e4a', accent: '#0d9488', mutedInk: '#0f766e' },
  purple:  { bg: '#faf5ff', border: '#d8b4fe', ink: '#581c87', accent: '#9333ea', mutedInk: '#6b21a8' },
};

// ─── Helpers ──────────────────────────────────────────────────
const AU_HOLIDAYS_2026 = new Set([
  '2026-01-01','2026-01-26','2026-04-03','2026-04-06','2026-04-25','2026-06-08',
  '2026-12-25','2026-12-26','2026-12-28',
]);
const AU_HOLIDAYS_2027 = new Set([
  '2027-01-01','2027-01-26','2027-03-26','2027-03-29','2027-04-25','2027-04-26','2027-06-14',
  '2027-12-25','2027-12-27','2027-12-28',
]);
const isBusinessDay = (d) => {
  const dow = d.getDay();
  if (dow === 0 || dow === 6) return false;
  const iso = d.toISOString().slice(0, 10);
  return !AU_HOLIDAYS_2026.has(iso) && !AU_HOLIDAYS_2027.has(iso);
};
const businessDaysBetween = (fromIso, toIso) => {
  if (!fromIso) return 0;
  const from = new Date(fromIso); from.setHours(0, 0, 0, 0);
  const to = toIso ? new Date(toIso) : new Date();
  to.setHours(0, 0, 0, 0);
  if (to < from) return 0;
  let bd = 0;
  const d = new Date(from);
  d.setDate(d.getDate() + 1); // start counting from day after lodgement
  while (d <= to) {
    if (isBusinessDay(d)) bd += 1;
    d.setDate(d.getDate() + 1);
  }
  return bd;
};
const fmtDateShort = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso)
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return String(iso); }
};
// Full date INCLUDING year — used anywhere a date could plausibly be from a
// different year so the user isn't left guessing "1 Jan of which year?".
const fmtDateFull = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso)
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return String(iso); }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(iso); }
};

const SLA_STAGE_LABEL = {
  initial_assessment:   'Initial assessment',
  mir_assessment:       'MIR assessment',
  conditional_approval: 'Conditional approval',
  formal_approval:      'Formal approval',
  loan_documents:       'Loan documents',
  settlement_booking:   'Settlement booking',
};
const STATUS_LABEL = {
  lodged: 'Lodged with Lender',
  mir_outstanding: 'MIR Outstanding',
  waiting_on_client: 'Waiting on Client',
  waiting_on_derwent: 'Waiting on Derwent',
  waiting_on_third_party: 'Waiting on Third Party',
  all_mirs_satisfied: 'All MIRs Satisfied · Waiting on Lender',
  back_with_lender: 'Back with Lender',
  conditional_approval: 'Conditional Approval',
  formal_approval: 'Formal Approval',
  completed: 'Completed',
};

// SLA visual state derivation
function derive(pack) {
  const totalBd = (pack.sla || {})[pack.sla_stage] || 5;
  const dr = pack.days_remaining;
  const elapsed = (dr === null || dr === undefined) ? 0 : Math.max(0, totalBd - dr);
  const progressPct = totalBd > 0 ? Math.min(100, (elapsed / totalBd) * 100) : 0;
  const bdSinceLodgement = businessDaysBetween(pack.lodged_at);

  // Choose the visual SLA state — has to communicate BOTH sla_indicator AND status
  const status = pack.status;
  let stateKey, stateLabel, stateTone;
  if (pack.sla_indicator === 'overdue') {
    stateKey = 'overdue'; stateLabel = `Overdue by ${Math.abs(dr)} BD`; stateTone = 'rose';
  } else if (pack.sla_indicator === 'due_today') {
    stateKey = 'due_today'; stateLabel = 'Due today'; stateTone = 'amber';
  } else if (['waiting_on_client', 'waiting_on_derwent', 'waiting_on_third_party'].includes(status)) {
    stateKey = 'paused';
    stateLabel = status === 'waiting_on_client' ? 'Paused · Waiting on client'
      : status === 'waiting_on_derwent' ? 'Paused · Waiting on Derwent'
      : 'Paused · Waiting on 3rd party';
    stateTone = status === 'waiting_on_derwent' ? 'blue' : status === 'waiting_on_third_party' ? 'purple' : 'amber';
  } else if (status === 'all_mirs_satisfied' || status === 'back_with_lender') {
    stateKey = 'back_with_lender'; stateLabel = 'Back with lender'; stateTone = 'teal';
  } else if (dr !== null && dr !== undefined && dr === 1) {
    stateKey = 'approaching'; stateLabel = 'Approaching SLA · 1 BD left'; stateTone = 'amber';
  } else if (status === 'completed') {
    stateKey = 'complete'; stateLabel = 'File completed'; stateTone = 'emerald';
  } else if (dr === null || dr === undefined) {
    stateKey = 'unknown'; stateLabel = 'No SLA yet'; stateTone = 'slate';
  } else {
    stateKey = 'on_track'; stateLabel = 'On track'; stateTone = 'emerald';
  }

  // Next action derivation
  //
  // Purpose: surface the ONE most important thing the team should do right now
  // on this file. Priority order:
  //   1. If there's an open MIR → the MOST URGENT one (overdue first, then
  //      soonest-due, then oldest-received) becomes the next action.
  //   2. Otherwise → derive from pack.status (lodged / back_with_lender / etc.).
  const mirs = pack.mirs || [];
  const openMirs = mirs.filter(m => !m.completed);
  const mirUrgencyRank = (m) => {
    const dueIn = m.due_date ? Math.floor((new Date(String(m.due_date).length === 10 ? `${m.due_date}T23:59:59` : m.due_date).getTime() - Date.now()) / 86_400_000) : null;
    const age = businessDaysBetween(m.date_received || m.created_at) || 0;
    const isOverdue = dueIn !== null && dueIn < 0;
    const isDueSoon = dueIn !== null && dueIn >= 0 && dueIn <= 1;
    // Lower = more urgent
    return (isOverdue ? -1_000_000 + dueIn : 0)  // overdue: more days overdue = more urgent
         + (isDueSoon ? -10_000 : 0)
         + (dueIn !== null ? dueIn * 10 : 0)
         - age;
  };
  const oldestOpenMir = openMirs.slice().sort((a, b) => mirUrgencyRank(a) - mirUrgencyRank(b))[0];

  // Compute a HUMAN-READABLE progress label for the "next action" line.
  // For an open MIR, prefer overdue/due-in-N context over the ambiguous
  // "N BD outstanding" of the past (which conflated received-age with due-age).
  const mirAgeLabel = (() => {
    if (!oldestOpenMir) return null;
    if (oldestOpenMir.due_date) {
      const dueIn = Math.floor((new Date(String(oldestOpenMir.due_date).length === 10 ? `${oldestOpenMir.due_date}T23:59:59` : oldestOpenMir.due_date).getTime() - Date.now()) / 86_400_000);
      if (dueIn < 0) return `Overdue by ${Math.abs(dueIn)} day${Math.abs(dueIn) === 1 ? '' : 's'}`;
      if (dueIn === 0) return 'Due today';
      if (dueIn === 1) return 'Due tomorrow';
      return `Due in ${dueIn} days`;
    }
    const rec = businessDaysBetween(oldestOpenMir.date_received || oldestOpenMir.created_at);
    return rec === 0 ? 'Came in today' : `${rec} BD since received`;
  })();

  let action;
  if (status === 'completed') {
    action = { title: 'File is complete', owner: '—', ownerTone: 'slate', ownerIcon: CheckCircle2, dueLabel: 'Closed', ageLabel: null };
  } else if (openMirs.length > 0) {
    action = {
      title: oldestOpenMir?.description
        ? `Respond to MIR: ${oldestOpenMir.description.slice(0, 80)}${oldestOpenMir.description.length > 80 ? '…' : ''}`
        : 'Respond to outstanding lender MIRs',
      owner: oldestOpenMir?.person_responsible || pack.credit_team_member || pack.broker,
      ownerTone: 'amber', ownerIcon: HardHat,
      dueLabel: oldestOpenMir?.due_date ? fmtDateFull(oldestOpenMir.due_date) : 'ASAP',
      ageLabel: mirAgeLabel,
      cta: 'Open MIR list',
      ctaAction: 'mirs',
    };
  } else if (status === 'waiting_on_client') {
    action = { title: 'Chase the client for outstanding docs / info', owner: pack.client_name, ownerTone: 'amber', ownerIcon: User,
      dueLabel: 'ASAP', ageLabel: `${bdSinceLodgement} BD since lodgement` };
  } else if (status === 'waiting_on_derwent') {
    action = { title: 'Complete the outstanding Derwent action', owner: pack.credit_team_member || 'Derwent', ownerTone: 'blue', ownerIcon: HardHat,
      dueLabel: 'ASAP', ageLabel: null };
  } else if (status === 'waiting_on_third_party') {
    action = { title: 'Chase the third party (accountant / employer / agent)', owner: pack.broker, ownerTone: 'purple', ownerIcon: Building2,
      dueLabel: 'ASAP', ageLabel: null };
  } else if (status === 'lodged') {
    action = { title: `Wait for initial assessment from ${pack.lender}`, owner: pack.lender, ownerTone: 'blue', ownerIcon: Landmark,
      dueLabel: fmtDateShort(pack.expected_response_date), ageLabel: dr !== null && dr !== undefined ? `${dr} BD remaining` : null };
  } else if (status === 'back_with_lender' || status === 'all_mirs_satisfied') {
    action = { title: `MIR assessment in progress with ${pack.lender}`, owner: pack.lender, ownerTone: 'teal', ownerIcon: Landmark,
      dueLabel: fmtDateShort(pack.expected_response_date), ageLabel: dr !== null && dr !== undefined ? `${dr} BD remaining` : null };
  } else if (status === 'conditional_approval') {
    action = { title: 'Progress to formal approval', owner: pack.credit_team_member || pack.broker, ownerTone: 'emerald', ownerIcon: FileSignature,
      dueLabel: fmtDateShort(pack.expected_response_date), ageLabel: null };
  } else if (status === 'formal_approval') {
    action = { title: 'Prepare loan documents & book settlement', owner: pack.broker, ownerTone: 'emerald', ownerIcon: FileSignature,
      dueLabel: fmtDateShort(pack.expected_response_date), ageLabel: null };
  } else {
    action = { title: 'File is progressing normally', owner: pack.broker, ownerTone: 'slate', ownerIcon: User, dueLabel: '—', ageLabel: null };
  }

  return { totalBd, elapsed, progressPct, bdSinceLodgement, stateKey, stateLabel, stateTone, action, mirs, openMirs };
}

// ─── 1. Application Overview strip ─────────────────────────────
// Light pastel palette matching the "Today's Focus" reference — dark
// slate chip top-left, date top-right, big client name, pastel fact
// cards (peach / mint / lavender / sky) instead of a dark hero.
function ApplicationOverview({ pack, derived, onUpdate }) {
  const loanAmount = (pack.answers || {}).loan_amount || '';
  return (
    <div
      className="rounded-2xl relative"
      data-testid="pl-application-overview"
      style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 4px 20px -12px rgba(15,23,42,0.08)',
      }}
    >
      <div className="p-4 md:p-4 relative">
        {/* Top row: dark chip + last-updated meta */}
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
            style={{ background: '#0f172a', color: '#ffffff' }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: '#34d399', boxShadow: '0 0 0 3px rgba(52,211,153,0.20)' }}
            />
            <span className="text-[9.5px] font-black tracking-[0.20em]">POST-LODGEMENT COMMAND CENTRE</span>
          </span>
          <span className="text-[10px] font-semibold" style={{ color: '#64748b' }}>
            Last updated <span style={{ color: '#334155' }}>{fmtDateTime(pack.last_updated_at)}</span> · by <span style={{ color: '#334155' }}>{pack.last_updated_by_name || '—'}</span>
          </span>
        </div>

        {/* Main hero row: applicant + loan amount */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-3 mb-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.16em]" style={{ color: '#64748b' }}>Applicant(s)</div>
            <div className="text-2xl md:text-3xl font-black tracking-tight mt-0.5" style={{ color: '#0f172a' }}>
              {pack.client_name}
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <LenderLogo lender={pack.lender} size="md" />
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                style={{ background: '#f1f5f9', color: '#0f172a' }}
              >
                <span className="text-[9px] font-black uppercase tracking-wider" style={{ color: '#64748b' }}>Status</span>
                <span className="text-[11px] font-black">{STATUS_LABEL[pack.status] || pack.status}</span>
              </span>
            </div>
          </div>
          {/* Loan amount pastel card */}
          <div className="rounded-xl p-3" style={{ background: '#ffedd5' }}>
            <div
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.85)' }}
            >
              <DollarSign className="w-3 h-3" style={{ color: '#9a3412' }} />
              <span className="text-[9px] font-black uppercase tracking-[0.14em]" style={{ color: '#9a3412' }}>Loan Amount</span>
            </div>
            <div className="mt-1.5 flex items-center gap-1">
              <span className="text-lg font-black tabular-nums" style={{ color: '#7c2d12' }}>$</span>
              <Input
                data-testid="pl-loan-amount"
                value={loanAmount}
                placeholder="e.g. 850,000"
                onChange={e => onUpdate?.({ answers: { loan_amount: e.target.value } })}
                className="h-7 bg-transparent border-0 text-xl font-black tabular-nums px-1"
                style={{ color: '#7c2d12', outline: 'none', boxShadow: 'none' }}
              />
            </div>
            <div className="text-[10px] font-semibold mt-0" style={{ color: '#c2410c' }}>Freely editable · optional</div>
          </div>
        </div>

        {/* Key facts grid — pastel cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <FactCard tone="sky"      icon={User}  label="Broker"        value={pack.broker || '—'}             sub={pack.credit_team_member ? `Credit · ${pack.credit_team_member}` : 'No credit assigned'} />
          <FactCard tone="rose"     icon={Send}  label="Lodged"        value={pack.lodged_at ? new Date(pack.lodged_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
            sub={pack.lodged_at ? new Date(pack.lodged_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : ''} />
          <FactCard tone="amber"    icon={Timer} label="Since Lodged"  value={`${derived.bdSinceLodgement}`}  sub="Business days" />
          <FactCard tone="mint"     icon={Flag}  label="SLA Stage"     value={SLA_STAGE_LABEL[pack.sla_stage] || '—'} sub={`${derived.totalBd} BD SLA`} />
        </div>
      </div>
    </div>
  );
}

// Shared pastel palette (mirrors PackDashboardSummary).
const OVERVIEW_PASTEL = {
  peach:    { bg: '#ffedd5', ink: '#7c2d12', label: '#9a3412', sub: '#c2410c', chip: 'rgba(255,255,255,0.85)' },
  mint:     { bg: '#d1fae5', ink: '#064e3b', label: '#047857', sub: '#059669', chip: 'rgba(255,255,255,0.85)' },
  lavender: { bg: '#ede9fe', ink: '#4c1d95', label: '#6d28d9', sub: '#7c3aed', chip: 'rgba(255,255,255,0.85)' },
  sky:      { bg: '#dbeafe', ink: '#1e3a8a', label: '#1d4ed8', sub: '#2563eb', chip: 'rgba(255,255,255,0.85)' },
  rose:     { bg: '#ffe4e6', ink: '#881337', label: '#9f1239', sub: '#be123c', chip: 'rgba(255,255,255,0.85)' },
  amber:    { bg: '#fef3c7', ink: '#78350f', label: '#92400e', sub: '#b45309', chip: 'rgba(255,255,255,0.85)' },
};

function FactCard({ tone, icon: Icon, label, value, sub }) {
  const c = OVERVIEW_PASTEL[tone] || OVERVIEW_PASTEL.mint;
  return (
    <div className="rounded-lg p-2" style={{ background: c.bg, colorScheme: 'light' }}>
      <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full" style={{ background: c.chip }}>
        <Icon className="w-2.5 h-2.5" style={{ color: c.label }} />
        <span className="text-[9px] font-black uppercase tracking-[0.12em]" style={{ color: c.label }}>{label}</span>
      </div>
      <div className="text-[13px] font-black tabular-nums leading-tight truncate mt-1" style={{ color: c.ink }}>{value}</div>
      {sub && <div className="text-[9.5px] font-semibold mt-0 truncate" style={{ color: c.sub }}>{sub}</div>}
    </div>
  );
}

// ─── STEP 1. SLA Countdown Card ────────────────────────────────
function SlaCountdownCard({ pack, derived }) {
  const t = T[derived.stateTone] || T.slate;
  const dr = pack.days_remaining;
  const isOverdue = derived.stateKey === 'overdue';
  const isPaused = derived.stateKey === 'paused' || derived.stateKey === 'back_with_lender';

  const StateIcon = derived.stateKey === 'overdue' ? AlertTriangle
    : derived.stateKey === 'due_today' ? Clock
    : derived.stateKey === 'approaching' ? Clock
    : derived.stateKey === 'on_track' ? CheckCircle2
    : derived.stateKey === 'paused' ? PauseCircle
    : derived.stateKey === 'back_with_lender' ? Landmark
    : derived.stateKey === 'complete' ? Award
    : Timer;

  return (
    <div className="rounded-xl border shadow-sm overflow-hidden bg-white"
      data-testid="pl-sla-countdown"
      style={{ borderColor: ZONE_STYLE.sla.border }}>
      <ZoneHeader
        step={1}
        icon={Timer}
        title="Lender SLA Countdown"
        subtitle={`${SLA_STAGE_LABEL[pack.sla_stage] || 'Awaiting SLA start'} · ${derived.totalBd} BD SLA`}
        {...ZONE_STYLE.sla}
        testid="pl-sla-header"
        compact
        right={
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-black text-[9.5px] uppercase tracking-widest" style={{ background: t.accent, color: '#ffffff' }}>
            <StateIcon className="w-2.5 h-2.5" />
            {derived.stateLabel}
          </div>
        }
      />

      {/* Big number + progress bar */}
      <div className="p-3.5">
        <div className="flex items-end justify-between mb-2 gap-3">
          <div className="flex items-baseline gap-1.5">
            {isPaused ? (
              <span className="text-2xl font-black tabular-nums" style={{ color: t.ink }}>Paused</span>
            ) : dr === null || dr === undefined ? (
              <span className="text-2xl font-black" style={{ color: t.mutedInk }}>—</span>
            ) : (
              <>
                <span className="text-4xl font-black tabular-nums leading-none" style={{ color: t.ink }}>
                  {isOverdue ? Math.abs(dr) : Math.max(0, dr)}
                </span>
                <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: t.mutedInk }}>
                  {isOverdue ? 'BD overdue' : dr === 0 ? 'DUE' : 'BD remaining'}
                </span>
              </>
            )}
          </div>
          <div className="text-right">
            <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: t.mutedInk }}>Expected response</div>
            <div className="text-[13px] font-black tabular-nums" style={{ color: t.ink }}>
              {pack.expected_response_date
                ? new Date(pack.expected_response_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
                : '—'}
            </div>
          </div>
        </div>

        {/* Progress meter */}
        <div className="mb-1.5">
          <div className="h-2 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
            <div className="h-full transition-all"
              style={{
                width: `${Math.min(100, derived.progressPct)}%`,
                background: isOverdue ? '#dc2626' : derived.stateKey === 'due_today' ? '#d97706' : derived.stateKey === 'approaching' ? '#f59e0b' : t.accent,
              }} />
          </div>
          <div className="flex justify-between mt-0.5 text-[9.5px] font-bold" style={{ color: t.mutedInk }}>
            <span>Start · {pack.mir_reset_at ? fmtDateShort(pack.mir_reset_at) : (pack.lodged_at ? new Date(pack.lodged_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—')}</span>
            <span className="tabular-nums">{derived.elapsed} / {derived.totalBd} BD elapsed</span>
          </div>
        </div>

        {/* Stage strip — visual timeline of SLA stages */}
        <StageStrip pack={pack} derived={derived} />
      </div>
    </div>
  );
}

// A compact visualisation of all 6 SLA stages with current highlighted
const SLA_STAGES_IN_ORDER = [
  ['initial_assessment', 'Initial'],
  ['mir_assessment', 'MIR'],
  ['conditional_approval', 'Cond.'],
  ['formal_approval', 'Formal'],
  ['loan_documents', 'Docs'],
  ['settlement_booking', 'Settle'],
];
function StageStrip({ pack, derived }) {
  const currentIdx = SLA_STAGES_IN_ORDER.findIndex(([k]) => k === pack.sla_stage);
  return (
    <div className="mt-3 flex items-stretch gap-1" data-testid="pl-stage-strip">
      {SLA_STAGES_IN_ORDER.map(([key, label], i) => {
        const isCurrent = i === currentIdx;
        const isPast = currentIdx >= 0 && i < currentIdx;
        const isFuture = currentIdx >= 0 && i > currentIdx;
        const bg = isCurrent ? derived.stateTone === 'rose' ? '#dc2626' : derived.stateTone === 'amber' ? '#d97706' : '#0f172a'
          : isPast ? '#10b981'
          : '#e2e8f0';
        const ink = isCurrent || isPast ? '#ffffff' : '#64748b';
        return (
          <div key={key} className="flex-1 min-w-0 rounded-md px-1.5 py-1.5 text-center transition-all"
            style={{ background: bg, opacity: isFuture ? 0.65 : 1 }}>
            <div className="text-[9px] font-black uppercase tracking-wider truncate" style={{ color: ink }}>{label}</div>
            <div className="text-[10px] font-bold tabular-nums mt-0.5" style={{ color: ink }}>{(pack.sla || {})[key] ?? 5} BD</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── 3. Next Action Panel ──────────────────────────────────────
function NextActionPanel({ pack, derived, onFocusMirs }) {
  const a = derived.action;
  const OwnerIcon = a.ownerIcon;
  const t = T[a.ownerTone] || T.slate;
  const toneKey = a.ownerTone || 'slate';
  const zoneTone = NEXT_ACTION_TONE[toneKey] || NEXT_ACTION_TONE.slate;
  return (
    <div className="rounded-xl border shadow-sm overflow-hidden bg-white"
      data-testid="pl-next-action" style={{ borderColor: zoneTone.border }}>
      <ZoneHeader
        step={2}
        icon={Zap}
        title="Next Action"
        subtitle="Auto-picked — the single most urgent thing on this file right now"
        {...zoneTone}
        testid="pl-next-action-header"
        compact
      />
      <div className="p-3.5">
        <div className="text-[13px] font-black leading-snug" style={{ color: t.ink }}>{a.title}</div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="rounded-md p-2" style={{ background: t.bg, border: `1px solid ${t.border}` }}>
            <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: t.mutedInk }}>Responsible</div>
            <div className="mt-0.5 flex items-center gap-1">
              <OwnerIcon className="w-3 h-3" style={{ color: t.accent }} />
              <span className="text-[12px] font-black truncate" style={{ color: t.ink }}>{a.owner || '—'}</span>
            </div>
          </div>
          <div className="rounded-md p-2" style={{ background: t.bg, border: `1px solid ${t.border}` }}>
            <div className="text-[9px] font-black uppercase tracking-widest" style={{ color: t.mutedInk }}>Due</div>
            <div className="mt-0.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" style={{ color: t.accent }} />
              <span className="text-[12px] font-black" style={{ color: t.ink }}>{a.dueLabel || '—'}</span>
            </div>
          </div>
        </div>
        {a.ageLabel && (() => {
          const isOverdue = a.ageLabel.startsWith('Overdue');
          const isDueVerySoon = a.ageLabel === 'Due today' || a.ageLabel === 'Due tomorrow';
          const bg = isOverdue ? '#fee2e2' : isDueVerySoon ? '#fef3c7' : 'transparent';
          const fg = isOverdue ? '#991b1b' : isDueVerySoon ? '#92400e' : t.mutedInk;
          return (
            <div
              className={`mt-1.5 text-[10.5px] font-black uppercase tracking-wider ${isOverdue ? 'animate-pulse' : ''}`}
              style={{
                color: fg,
                background: bg,
                padding: bg === 'transparent' ? '0' : '2px 8px',
                borderRadius: 999,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
              data-testid="pl-next-action-age-label"
            >
              {isOverdue ? <Flame className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
              {a.ageLabel}
            </div>
          );
        })()}
        {a.cta && (
          <button
            type="button"
            onClick={() => a.ctaAction === 'mirs' && onFocusMirs?.()}
            className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-black uppercase tracking-widest transition-all hover:opacity-90"
            style={{ background: t.accent, color: '#ffffff' }}
            data-testid="pl-next-action-cta"
          >
            {a.cta} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── 4. Application Timeline ──────────────────────────────────
function ApplicationTimeline({ pack, step = 4 }) {
  const events = useMemo(() => buildTimeline(pack), [pack]);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('lp-timeline-collapsed') === '1'
  );
  const toggle = () => {
    setCollapsed((c) => {
      const next = !c;
      localStorage.setItem('lp-timeline-collapsed', next ? '1' : '0');
      return next;
    });
  };
  return (
    <div className="rounded-xl border shadow-sm overflow-hidden bg-white" data-testid="pl-timeline" style={{ borderColor: ZONE_STYLE.timeline.border }}>
      <ZoneHeader
        step={step}
        icon={ClipboardCheck}
        title="Application Timeline"
        subtitle={`${events.length} event${events.length === 1 ? '' : 's'} in order${collapsed ? ' · collapsed' : ''}`}
        {...ZONE_STYLE.timeline}
        testid="pl-timeline-header"
        compact
        collapsible
        collapsed={collapsed}
        onToggle={toggle}
        right={
          <span className="text-[9.5px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: ZONE_STYLE.timeline.accent, color: '#ffffff' }}>
            {events.length}
          </span>
        }
      />
      {!collapsed && (
        <div className="p-3.5">
          {events.length === 0 ? (
            <div className="text-center py-4 text-[11.5px] italic text-slate-500">No timeline events yet.</div>
          ) : (
            <ol className="relative border-l-2 ml-1.5" style={{ borderColor: '#e2e8f0' }}>
            {events.map((e, i) => {
              const t = T[e.tone] || T.slate;
              const Icon = e.icon;
              return (
                <li key={i} className="ml-4 pb-2.5 relative" data-testid={`pl-timeline-item-${i}`}>
                  {/* Dot */}
                  <div className="absolute -left-[24px] top-0.5 w-5 h-5 rounded-full flex items-center justify-center shadow-sm" style={{ background: t.accent, border: '2px solid #ffffff' }}>
                    <Icon className="w-2.5 h-2.5" style={{ color: '#ffffff' }} />
                  </div>
                  {/* Compact single-line card */}
                  <div className="rounded-md border px-2.5 py-1.5" style={{ background: t.bg, borderColor: t.border }}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-[11.5px] font-black leading-snug truncate" style={{ color: t.ink }}>{e.title}</div>
                      <div className="text-[9.5px] font-bold tabular-nums shrink-0" style={{ color: t.mutedInk }}>
                        {e.ts ? new Date(e.ts).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </div>
                    </div>
                    {(e.who || e.detail) && (
                      <div className="text-[10px] font-semibold mt-0.5 truncate" style={{ color: t.mutedInk }}>
                        {e.who && <>by <span style={{ color: t.ink }}>{e.who}</span></>}
                        {e.who && e.detail && ' · '}
                        {e.detail && <span>{e.detail}</span>}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
          )}
        </div>
      )}
    </div>
  );
}

function buildTimeline(pack) {
  const ev = [];
  const push = (o) => o.ts && ev.push(o);

  push({ icon: Play, title: 'Submission pack started', ts: pack.created_at, who: pack.created_by_name, tone: 'slate' });

  if (pack.lodged_at) {
    push({ icon: Send, title: 'Application lodged with lender', ts: pack.lodged_at, who: pack.lodged_by_name || pack.broker, tone: 'blue',
      detail: pack.application_number ? `App #${pack.application_number}` : null });
    const initBd = (pack.sla || {}).initial_assessment || 5;
    push({ icon: Timer, title: `Initial lender SLA started (${initBd} BD)`, ts: pack.lodged_at, tone: 'blue',
      detail: `Expected response ${pack.expected_response_date && new Date(pack.lodged_at + '') ? '' : ''}` });
  }

  // MIRs — one entry per lifecycle stage
  (pack.mirs || []).forEach((m, idx) => {
    const num = idx + 1;
    push({ icon: FileQuestion, title: `MIR #${num} received — ${m.description ? m.description.slice(0, 80) : 'unnamed'}`,
      ts: m.date_received || m.created_at, who: m.created_by_name, tone: 'amber' });
    if (m.person_responsible) {
      push({ icon: User, title: `MIR #${num} assigned to ${m.person_responsible}`, ts: m.updated_at, who: m.updated_by_name, tone: 'indigo' });
    }
    if (m.date_sent_back || m.sent_at) {
      push({ icon: Send, title: `MIR #${num} returned to lender`, ts: m.date_sent_back || m.sent_at, who: m.updated_by_name, tone: 'teal',
        detail: m.description ? m.description.slice(0, 90) : null });
    }
    if (m.completed && m.completed_at) {
      push({ icon: CheckCircle2, title: `MIR #${num} completed`, ts: m.completed_at, who: m.updated_by_name, tone: 'emerald' });
    }
  });

  if (pack.mir_reset_at) {
    push({ icon: RotateCcw, title: 'All MIRs satisfied — MIR-assessment SLA restarted', ts: pack.mir_reset_at, tone: 'teal',
      detail: pack.expected_response_date ? `New expected response ${fmtDateShort(pack.expected_response_date)}` : null });
  }

  // Status transitions from activity
  (pack.activity || []).forEach(a => {
    if (a.action === 'status_change') {
      const after = a.after;
      if (after === 'conditional_approval') push({ icon: ShieldCheck, title: 'Conditional approval received', ts: a.ts, who: a.user, tone: 'emerald' });
      else if (after === 'formal_approval') push({ icon: ShieldCheck, title: 'Formal approval received', ts: a.ts, who: a.user, tone: 'emerald' });
    }
  });

  if (pack.completed_at) {
    push({ icon: Award, title: 'File completed', ts: pack.completed_at, who: pack.last_updated_by_name, tone: 'emerald' });
  }

  // De-duplicate exact identical entries + sort chronologically
  const seen = new Set();
  const unique = ev.filter(e => {
    const k = `${e.ts}|${e.title}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  unique.sort((a, b) => new Date(a.ts) - new Date(b.ts));
  return unique;
}
